import React from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslator } from '@jutro/locale';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { StringUtil } from 'ml-portals-util-js';

import metadata from './MLKeyContactsComponent.metadata.json5';
import styles from '../MLClaimDetailPopup/MLClaimDetailPopup.module.scss';
import messages from './MLKeyContactsComponent.messages';

const MLKeyContactsComponent = (props) => {
    const {
        adjuster,
        claimContacts,
        policyType,
        formatPhoneNumber
    } = props;
    
    const translator = useTranslator();
    const namedInsured = claimContacts.find((contact) => contact.contactRoles.includes('insured'));
    
    let appraisalSources;
    if (policyType === 'PersonalAuto') {
        appraisalSources = claimContacts.filter((contact) => contact.contactDTO.contactType === 'CompanyVendor');
    }
    let serviceProviders;
    if (policyType === 'HOPHomeowners') {
        serviceProviders = claimContacts.filter((contact) => contact.contactDTO.contactType === 'CompanyVendor');
    }
    
    const formatAddressLine1 = (person) => {
        if (!_.isUndefined(person.contactDTO) && !_.isUndefined(person.contactDTO.primaryAddress)) {
            return person.contactDTO.primaryAddress.addressLine1;
        }
    };

    const formatAddressLine2 = (person) => {
        if (!_.isUndefined(person.contactDTO) && !_.isUndefined(person.contactDTO.primaryAddress)) {
            const cityParam = person.contactDTO.primaryAddress.city;
            const city = cityParam ? cityParam.concat(',') : cityParam;
            const state = person.contactDTO.primaryAddress.state;
            const postalCode = person.contactDTO.primaryAddress.postalCode;
            const result = [city, state, postalCode].join(' ');
            return result;
        }
    };

    const formatName = (person) => {
        if (!_.isUndefined(person.contactDTO)) {
            let firstName = person.contactDTO.firstName.toLowerCase();
            let lastName = person.contactDTO.lastName.toLowerCase();
            firstName = StringUtil.capitalizeFirstLetter(firstName);
            lastName = StringUtil.capitalizeFirstLetter(lastName);
            return firstName.concat(' ', lastName);
        }
    };

    const getVendorOverrides = () => {
        const appriasalSourceOverrides = _.isUndefined(appraisalSources) ? [] : appraisalSources.map((appraiser, index) => {
            return {
                [`appraisalSourceName${index}`]: {
                    content: (
                        <b>
                            {appraiser.contactDTO.displayName}
                        </b>
                    )
                },
                [`appraisalSourceAddressLin1${index}`]: {
                    content: formatAddressLine1(appraiser),
                    visible: formatAddressLine1(appraiser) !== undefined

                },
                [`appraisalSourceAddressLin2${index}`]: {
                    content: formatAddressLine2(appraiser),
                    visible: formatAddressLine2(appraiser) !== undefined
                },
                [`appraisalSourcePhone${index}`]: {
                    content: `${translator(messages.conatatPhone)} ${formatPhoneNumber(appraiser.contactDTO.contactNumber_ML)}`,
                    visible: appraiser.contactDTO.contactNumber_ML !== undefined
                },
                [`appraisalSourceFax${index}`]: {
                    content: `${translator(messages.ConatactFax)} ${formatPhoneNumber(appraiser.contactDTO.fax_ML)}`,
                    visible: appraiser.contactDTO.fax_ML !== undefined
                },
                [`appraisalSourceEmailAddress${index}`]: {
                    content: translator(messages.conatactEmail, { email: appraiser.contactDTO.emailAddress1 }),
                    visible: appraiser.contactDTO.emailAddress1 !== undefined
                }
            }
        });

        const serviceProviderOverrides = _.isUndefined(serviceProviders) ? [] : serviceProviders.map((serviceProvider, index) => {
            return {
                [`serviceProviderName${index}`]: {
                    content: (
                        <b>
                            {serviceProvider.contactDTO.displayName}
                        </b>
                    )
                },
                [`serviceProviderAddressLin1${index}`]: {
                    content: formatAddressLine1(serviceProvider),
                    visible: formatAddressLine1(serviceProvider) !== undefined
                },
                [`serviceProviderAddressLin2${index}`]: {
                    content: formatAddressLine2(serviceProvider),
                    visible: formatAddressLine2(serviceProvider)  !== undefined
                },
                [`serviceProviderPhone${index}`]: {
                    content: `${translator(messages.conatatPhone)} ${formatPhoneNumber(serviceProvider.contactDTO.contactNumber_ML)}`,
                    visible: serviceProvider.contactDTO.contactNumber_ML  !== undefined
                },
                [`serviceProviderFax${index}`]: {
                    content: `${translator(messages.ConatactFax)} ${formatPhoneNumber(serviceProvider.contactDTO.fax_ML)}`,
                    visible: serviceProvider.contactDTO.fax_ML  !== undefined
                },
                [`serviceProviderEmailAddress${index}`]: {
                    content: translator(messages.conatactEmail, { email: serviceProvider.contactDTO.emailAddress1 }),
                    visible: serviceProvider.contactDTO.emailAddress1  !== undefined
                }
            }
        });

        return Object.assign({}, ...appriasalSourceOverrides, ...serviceProviderOverrides);
    };


    const overrideProps = {
        namedInsuredName: {
            content: (
                <b>
                    {formatName(namedInsured)}
                </b>
            )
        },
        namedInsuredAddressLin1: {
            content: formatAddressLine1(namedInsured),
        },
        namedInsuredAddressLin2: {
            content: formatAddressLine2(namedInsured)
        },
        namedInsuredPhone: {
            content: `${translator(messages.conatatPhone)} ${formatPhoneNumber(namedInsured.contactDTO.contactNumber_ML)}`
        },
        namedInsuredEmailAddress: {
            content: translator(messages.conatactEmail, { email: namedInsured.contactDTO.emailAddress1 })
        },
        adjusterColumnContainer: {
            visible: adjuster !== undefined && !_.isEmpty(adjuster)
        },
        adjusterName: {
            content: (
                <b>
                    {adjuster.displayName}
                </b>
            )
        },
        adjusterAddressLin1: {
            content: formatAddressLine1(adjuster),
            visible: adjuster.addressLine1 !== undefined
        },
        adjusterAddressLin2: {
            content: formatAddressLine2(adjuster),
            visible: formatAddressLine2(adjuster) !== undefined

        },
        adjusterPhone: {
            content: `${translator(messages.conatatPhone)} ${formatPhoneNumber(adjuster.phoneNumber)}`,
            visible: adjuster.phoneNumber !== undefined
        },
        adjusterFax: {
            content: `${translator(messages.ConatactFax)} ${formatPhoneNumber(adjuster.fax_ML)}`,
            visible: !_.isUndefined(adjuster.fax_ML)
        },
        adjusterEmailAddress: {
            content: translator(messages.conatactEmail, { email: adjuster.email }),
            visible: !_.isUndefined(adjuster.email)
        },
        appraisalSourcesContainer : {
            data : appraisalSources
        },
        serviceProviderContainer : {
            data: serviceProviders
        },

        ...getVendorOverrides()

    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            classNameMap={styles}
        />
    );
};

MLKeyContactsComponent.propTypes = {
    adjuster: PropTypes.object.isRequired,
    claimContacts: PropTypes.arrayOf(PropTypes.object).isRequired,
    policyType: PropTypes.string.isRequired

};

export default MLKeyContactsComponent;
